import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import Seo from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FaqAccordion from '@/components/FaqAccordion';
import BlogBlocks from '@/components/blog/BlogBlocks';
import BlogCard from '@/components/blog/BlogCard';
import { guideSrc, headingId } from '@/lib/content';
import NotFound from '@/pages/NotFound';
import { formatDate, getPost } from '@/data/blog/posts';
import { articleJsonLd, breadcrumb, faqPageJsonLd } from '@/lib/blog-structured-data';

/* =====================================================================
   BlogPost → route: /blog/:slug
   Başlık + içindekiler (h2'lerden) + içerik blokları + yazı sonu SSS
   + ilgili yazılar. Şema: BlogPosting + BreadcrumbList + FAQPage.
   ===================================================================== */

const SITE = 'https://tilkiotomasyon.com';

// Her yazıda kullanım rehberine yönlendiren kart (lg: yan sütun, altı: yazı sonu).
const GuideCard = ({ className = '' }) => (
  <Link
    to="/nasil-kullanilir"
    className={`block rounded-2xl border border-hairline bg-surface-2 p-4 transition-colors hover:border-brand-200 ${className}`}
  >
    <span className="mb-1.5 inline-flex items-center font-poppins text-sm font-semibold text-ink-strong">
      <BookOpen className="mr-1.5 h-4 w-4 text-brand-600" aria-hidden="true" />
      Kullanım rehberi
    </span>
    <span className="block font-inter text-sm leading-relaxed text-ink-muted">
      Tilki Otomasyon’u kurulumdan siparişe ekran görüntüleriyle adım adım görün.
    </span>
  </Link>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;

  const toc = post.blocks.filter((b) => b.t === 'h2');
  const related = post.related.map(getPost).filter(Boolean);
  // Şema görseli: yazıdaki ilk büyük ekran görüntüsü, yoksa kapak.
  const firstGuide = post.blocks.find((b) => b.t === 'guide');
  const schemaImage = firstGuide ? guideSrc(firstGuide.image, 1800) : post.cover.src;

  const jsonLd = [
    articleJsonLd({ ...post, image: `${SITE}${schemaImage}` }),
    breadcrumb([
      { name: 'Ana Sayfa', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
    ...(post.faq?.length ? [faqPageJsonLd(post.faq)] : []),
  ];

  return (
    <div className="min-h-screen bg-surface">
      <Seo
        title={post.metaTitle}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={`${SITE}${post.cover.src}`}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.updated}
        jsonLd={jsonLd}
      />
      <Header />

      <main id="main-content">
        <header className="border-b border-hairline bg-surface-2 py-10 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="mx-auto flex max-w-[1080px] items-center gap-10">
              <div className="min-w-0 flex-1">
                <nav aria-label="Sayfa yolu" className="mb-5">
                  <ol className="flex flex-wrap items-center gap-1 font-inter text-sm text-ink-muted">
                    <li>
                      <Link to="/" className="hover:text-ink-strong">
                        Ana Sayfa
                      </Link>
                    </li>
                    <li className="flex items-center gap-1">
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      <Link to="/blog" className="hover:text-ink-strong">
                        Blog
                      </Link>
                    </li>
                  </ol>
                </nav>
                <h1 className="type-display-2 text-balance mb-4">{post.title}</h1>
                <p className="font-inter text-sm text-ink-muted">
                  <time dateTime={post.date}>{formatDate(post.date)}</time> · {post.readMinutes} dk okuma · Tilki
                  Otomasyon
                </p>
              </div>
              <img
                src={post.cover.src}
                width={post.cover.width}
                height={post.cover.height}
                alt={post.cover.alt}
                className="hidden aspect-[4/5] w-44 flex-shrink-0 rounded-2xl border border-hairline object-cover shadow-card md:block"
              />
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-[1080px] grid-cols-1 gap-10 py-10 sm:py-14 lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-14">
            <article className="min-w-0 max-w-[760px]">
              {toc.length > 2 && (
                <details className="mb-8 rounded-2xl border border-hairline bg-surface-2 p-4 lg:hidden">
                  <summary className="cursor-pointer font-poppins font-semibold text-ink-strong">İçindekiler</summary>
                  <ol className="mt-3 space-y-2 pl-5 font-inter text-sm text-ink-muted [list-style:decimal]">
                    {toc.map((h) => (
                      <li key={h.text}>
                        <a href={`#${headingId(h.text)}`} className="hover:text-ink-strong">
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </details>
              )}

              <BlogBlocks blocks={post.blocks} />
              <GuideCard className="mt-10 lg:hidden" />

              {post.faq?.length > 0 && (
                <section className="mt-14" aria-labelledby="yazi-sss">
                  <h2 id="yazi-sss" className="type-h2 mb-5">
                    Sık sorulan sorular
                  </h2>
                  <FaqAccordion faqs={post.faq} />
                  <p className="mt-4 font-inter text-sm text-ink-muted">
                    Daha fazlası için{' '}
                    <Link to="/iletisim" className="font-medium text-brand-700 underline underline-offset-2">
                      bize ulaşın
                    </Link>
                    .
                  </p>
                </section>
              )}
            </article>

            <aside className="hidden space-y-6 lg:sticky lg:top-24 lg:block lg:self-start">
              {toc.length > 2 && (
                <nav aria-label="İçindekiler">
                  <p className="mb-3 font-inter text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted">
                    İçindekiler
                  </p>
                  <ol className="space-y-1">
                    {toc.map((h) => (
                      <li key={h.text}>
                        <a
                          href={`#${headingId(h.text)}`}
                          className="block rounded-lg px-3 py-1.5 font-inter text-sm text-ink-muted hover:bg-surface-2 hover:text-ink-strong"
                        >
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
              <GuideCard />
            </aside>
          </div>
        </div>

        {related.length > 0 && (
          <section className="border-t border-hairline bg-surface-2 py-12 sm:py-16">
            <div className="container mx-auto max-w-[1080px] px-4">
              <h2 className="type-h2 mb-6">İlgili yazılar</h2>
              <ul className="grid gap-4 md:grid-cols-2">
                {related.map((r) => (
                  <li key={r.slug}>
                    <BlogCard post={r} headingLevel="h3" />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
